import { TZDate, tzOffset } from '@date-fns/tz'
import { format } from 'date-fns'

/** The ONE place that turns an instant into a local calendar day or time (ARCHITECTURE 15.3). All instants are stored as
 *  timestamptz; local days always come from the site's time zone, never from toISOString().slice(0, 10) or the device zone. */
export const SITE_TZ_DEFAULT = 'Europe/Paris'

export function localDate(instant: Date | string | number, tz: string = SITE_TZ_DEFAULT): string {
  return format(new TZDate(new Date(instant), tz), 'yyyy-MM-dd')
}

export function formatLocal(instant: Date | string | number, tz: string = SITE_TZ_DEFAULT, pattern = 'dd/MM/yyyy HH:mm'): string {
  return format(new TZDate(new Date(instant), tz), pattern)
}

/** A wall-clock time in a zone, as an instant (ms), computed from the zone's offsets only, so the DEVICE's zone never enters (M11 DST pass: building a
 *  TZDate from a date string or from parts let the device zone decide a day's length, and which of the two 02:30 of the autumn night a form meant).
 *  A time that exists twice (the hour repeated when the clocks go back) is the FIRST one, summer time; a time that does not exist (the hour skipped when
 *  they go forward) is read with the offset before the jump, i.e. one hour later on the wall (02:30 -> 03:30), as Postgres reads it. */
function wallToMs(y: number, m: number, d: number, h: number, mi: number, tz: string): number {
  const wall = Date.UTC(y, m - 1, d, h, mi)
  const before = tzOffset(tz, new Date(wall - 86_400_000)), after = tzOffset(tz, new Date(wall + 86_400_000))
  const valid = [...new Set([before, after])].map((o) => wall - o * 60_000).filter((t) => wall - tzOffset(tz, new Date(t)) * 60_000 === t)
  return valid.length > 0 ? Math.min(...valid) : wall - before * 60_000
}

/** Hours in the site-local day: 23 or 25 on a DST change day, not the 24 between two UTC midnights. */
export function localDayLengthHours(day: string, tz: string = SITE_TZ_DEFAULT): number {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  return (wallToMs(y, m, d + 1, 0, 0, tz) - wallToMs(y, m, d, 0, 0, tz)) / 3_600_000
}

/** "il y a 3 min" style, language-neutral input: a duration in ms. */
export function ageMs(from: number | string | Date, now = Date.now()): number {
  return now - new Date(from).getTime()
}

/** A calendar day chosen in a date field, as an instant: midday in the SITE's zone (so it is the same local day wherever the device is).
 *  Today is better sent as "now": the caller omits the time for today. */
export function noonOfLocalDay(day: string, tz: string = SITE_TZ_DEFAULT): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  return new TZDate(y, m - 1, d, 12, 0, 0, tz).toISOString()
}

/** A wall-clock time ("07:30") on a local day, as an instant in the SITE's zone. The one way a form turns what a person typed into what is stored. */
export function localToInstant(day: string, hhmm: string, tz: string = SITE_TZ_DEFAULT): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  const [h, mi] = hhmm.split(':').map(Number) as [number, number]
  return new Date(wallToMs(y, m, d, h, mi, tz)).toISOString()          // UTC form: the same string whatever the device zone
}
